import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import AdvancedTable from "@/components/table/AdvancedTable";
import FastMultiSelect from "@/components/ui/FastMultiSelect";
import MultiSelect from "@/components/ui/multi-select";
import SearchableSelect from "@/components/ui/SearchableSelect";
import MeetingPreviewDialog from "@/components/MeetingPreviewDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Calendar, Globe, Lock, Repeat, Search, Plus, Video, Phone, MapPin, MessageCircle, Mail, MessageSquare, Building, Users, X, AlertCircle, AlertTriangle, Star, CalendarClock, EyeOff, Eye } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";

// Iter 253k · Communication modes — mirrors the backend `CommMode` literal.
// Each mode gates which extra fields the editor shows and which chip renders
// in the table.
export const COMM_MODES = [
  { value: "video_call", label: "Video call", icon: Video, color: "#2563eb" },
  { value: "audio_call", label: "Audio call", icon: Phone, color: "#0891b2" },
  { value: "in_person", label: "In-person", icon: MapPin, color: "#059669" },
  { value: "chat_dm", label: "Chat / DM", icon: MessageCircle, color: "#7c3aed" },
  { value: "email_thread", label: "Email thread", icon: Mail, color: "#dc2626" },
  { value: "whatsapp", label: "WhatsApp", icon: MessageSquare, color: "#16a34a" },
  { value: "site_visit", label: "Site visit", icon: Building, color: "#ea580c" },
  // R155 · Feb 2026 — Escalation touchpoint. Rose accent so it stands
  // out in the mode column of the interactions table.
  { value: "escalation", label: "Escalation", icon: AlertTriangle, color: "#e11d48" },
];
const MODE_BY_VALUE = Object.fromEntries(COMM_MODES.map((m) => [m.value, m]));

export default function AllMeetings() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  // Feb 2026 · Multi-select filters for Client + Project. Backend still
  // takes a single project_id so we filter client-side after the
  // workspace meetings fetch (which is capped at 200 rows). This also
  // lets us filter by Client without a schema change.
  const [projectIds, setProjectIds] = useState([]);
  const [clientIds, setClientIds] = useState([]);
  const [scope, setScope] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [loggedFilter, setLoggedFilter] = useState("");   // "" | "true" | "false"
  // R148g · Feb 2026 — Time range toggle with 'recent' (today + 7d back)
  // as default so users land on what matters most.
  const [timeRange, setTimeRange] = useState("recent");
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | meeting (edit)
  const [previewing, setPreviewing] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [reassignOpen, setReassignOpen] = useState(false);
  // R148h · Feb 2026 — Reschedule + Rate inline dialogs
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rateTarget, setRateTarget] = useState(null);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/projects`).then((r) => setProjects(r.data || []));
    api.get(`/workspaces/${activeWorkspaceId}/clients`).then((r) => setClients(r.data || [])).catch(() => {});
    api.get(`/workspaces/${activeWorkspaceId}/members`).then((r) => setMembers(r.data || [])).catch(() => {});
  }, [activeWorkspaceId]);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      // When exactly one project is picked we can leverage the backend
      // index. Multi-project selection filters client-side after fetch.
      if (projectIds.length === 1) params.set("project_id", projectIds[0]);
      if (scope) params.set("scope", scope);
      if (modeFilter) params.set("mode", modeFilter);
      if (loggedFilter) params.set("is_logged", loggedFilter);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/meetings?${params.toString()}`);
      setRows(data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId, q, projectIds, scope, modeFilter, loggedFilter, timeRange]);

  // R148g · Client-side time-range filter — cheap since the list is
  // capped to 200 rows server-side. Backend already returns the whole
  // workspace so we can slice locally without an extra round-trip.
  const nowMs = Date.now();
  const DAY = 24 * 60 * 60 * 1000;
  const rangeFn = {
    all:        () => true,
    past:       (m) => new Date(m.scheduled_at).getTime() < nowMs,
    today:      (m) => new Date(m.scheduled_at).toDateString() === new Date().toDateString(),
    recent:     (m) => {
      const t = new Date(m.scheduled_at).getTime();
      return t <= nowMs && t >= nowMs - 7 * DAY;
    },
    upcoming:   (m) => {
      const t = new Date(m.scheduled_at).getTime();
      return t >= nowMs && t <= nowMs + 7 * DAY;
    },
    this_month: (m) => {
      const d = new Date(m.scheduled_at); const now = new Date();
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    },
    next_month: (m) => {
      const d = new Date(m.scheduled_at); const now = new Date();
      const nm = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return d.getFullYear() === nm.getFullYear() && d.getMonth() === nm.getMonth();
    },
  };
  const filteredRows = useMemo(() => {
    // multi-project, then multi-client. We cache the sets so toggling
    // one filter doesn't re-walk the others' includes() per row.
    const rangeF = rangeFn[timeRange] || rangeFn.recent;
    const projSet = projectIds.length > 1 ? new Set(projectIds) : null;
    const clientSet = clientIds.length > 0 ? new Set(clientIds) : null;
    // Build project→client lookup for rows that only carry project_id.
    const projToClient = new Map(projects.map((p) => [p.id, p.client_id]));
    const out = [];
    for (const r of rows) {
      if (!rangeF(r)) continue;
      if (projSet && !projSet.has(r.project_id)) continue;
      if (clientSet) {
        const cid = r.client_id || projToClient.get(r.project_id);
        if (!cid || !clientSet.has(cid)) continue;
      }
      out.push(r);
    }
    return out;
  }, [rows, timeRange, projectIds, clientIds, projects]);

  // Pre-sorted, memoised option arrays passed to FastMultiSelect so the
  // dropdowns don't re-sort on every parent render (that was the main
  // reason the old project <Select> felt sluggish — N re-renders × N
  // DOM-mounted items). FastMultiSelect also internally caps + filters.
  const clientOptions = useMemo(
    () => (clients || []).map((c) => ({ value: c.id, label: c.name })),
    [clients]
  );
  const projectOptions = useMemo(
    () => (projects || []).map((p) => ({ value: p.id, label: p.name, hint: p.client_name || "" })),
    [projects]
  );

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} meeting(s)? Action items will be unlinked but kept.`)) return;
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/meetings/bulk-delete`, { ids });
      toast.success(`Deleted ${data.deleted}${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk delete failed")); }
  };

  const bulkReassign = async (ownerUserId) => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !ownerUserId) return;
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/meetings/bulk-reassign`, { ids, owner_user_id: ownerUserId });
      toast.success(`Reassigned ${data.updated}${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      setReassignOpen(false);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk reassign failed")); }
  };

  const columns = useMemo(() => ([
    {
      key: "title", label: "Meeting", width: 300, sortable: true, filterType: "text", frozen: true,
      accessor: (m) => m.title,
      render: (m) => {
        const missing = m.missing_details || [];
        return (
          <HoverCard openDelay={220} closeDelay={80}>
            <HoverCardTrigger asChild>
              <div className="min-w-0">
                <div className="font-medium truncate flex items-center gap-1">
                  {m.series_id && <Repeat className="w-3 h-3 text-muted-foreground shrink-0" />}
                  {m.is_private && <EyeOff className="w-3 h-3 text-purple-600 shrink-0" title="Private meeting" />}
                  <span className="truncate">{m.title}</span>
                  {typeof m.rating === "number" && m.rating > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 shrink-0" data-testid="meeting-rating-chip">
                      <Star className="w-3 h-3 fill-current" /> {m.rating}
                    </span>
                  )}
                  {missing.length > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-300 shrink-0"
                      title={`Missing: ${missing.join(", ")}`}
                      data-testid="meeting-missing-badge"
                    >
                      <AlertCircle className="w-3 h-3" /> incomplete
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {m.client_name ? <span className="text-foreground/70">{m.client_name}</span> : null}
                  {m.client_name && m.brand_name ? " · " : null}
                  {m.brand_name ? <span>{m.brand_name}</span> : null}
                  {(m.client_name || m.brand_name) && m.project_name ? " · " : null}
                  {m.project_name}
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 p-3 rounded-none" side="right" align="start">
              <div className="space-y-2">
                <div className="text-sm font-semibold">{m.title}</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                  <div className="text-muted-foreground">Host</div><div className="truncate">{m.host_name || "—"}</div>
                  <div className="text-muted-foreground">Client</div><div className="truncate">{m.client_name || "—"}</div>
                  <div className="text-muted-foreground">Brand</div><div className="truncate">{m.brand_name || "—"}</div>
                  <div className="text-muted-foreground">Project</div><div className="truncate">{m.project_name || "—"}</div>
                  <div className="text-muted-foreground">When</div>
                  <div className="truncate">{new Date(m.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</div>
                  <div className="text-muted-foreground">Duration</div><div>{m.duration_minutes || 30}m</div>
                  <div className="text-muted-foreground">Action items</div>
                  <div>
                    {(m.action_items_total || 0) === 0
                      ? "—"
                      : <span>{m.action_items_open || 0} open / {m.action_items_total} total</span>}
                  </div>
                  <div className="text-muted-foreground">Rating</div>
                  <div>
                    {typeof m.rating === "number" && m.rating > 0
                      ? <span className="text-amber-600 inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-current" /> {m.rating}/5</span>
                      : <span className="text-muted-foreground">unrated</span>}
                  </div>
                </div>
                {(m.attendee_user_ids?.length || m.external_attendees?.length) ? (
                  <div className="text-[11px] text-muted-foreground border-t pt-1.5">
                    {(m.attendee_user_ids || []).length} internal · {(m.external_attendees || []).length} external attendees
                  </div>
                ) : null}
                {missing.length > 0 && (
                  <div className="text-[11px] text-rose-700 border-t pt-1.5">
                    Missing: {missing.join(", ")}
                  </div>
                )}
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      },
    },
    {
      key: "scheduled_at", label: "When", width: 180, sortable: true, filterType: "date-range",
      accessor: (m) => new Date(m.scheduled_at).getTime(),
      render: (m) => (
        <div>
          <div className="text-xs">{new Date(m.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</div>
          <div className="text-[11px] text-muted-foreground">{m.duration_minutes || 30}m · {fmtRelative(m.scheduled_at)}</div>
        </div>
      ),
    },
    {
      key: "host_name", label: "Host", width: 140, sortable: true, filterType: "text",
      accessor: (m) => m.host_name || "",
      render: (m) => <span className="text-xs truncate">{m.host_name || "—"}</span>,
    },
    {
      key: "client_name", label: "Client", width: 140, sortable: true, filterType: "text", visible: false,
      accessor: (m) => m.client_name || "",
      render: (m) => <span className="text-xs truncate">{m.client_name || "—"}</span>,
    },
    {
      key: "brand_name", label: "Brand", width: 140, sortable: true, filterType: "text",
      accessor: (m) => m.brand_name || "",
      render: (m) => <span className="text-xs truncate">{m.brand_name || "—"}</span>,
    },
    {
      key: "project_name", label: "Project", width: 160, sortable: true, filterType: "text", visible: false,
      accessor: (m) => m.project_name || "",
      render: (m) => <span className="text-xs truncate">{m.project_name || "—"}</span>,
    },
    {
      key: "action_items", label: "Action items", width: 110, sortable: true,
      accessor: (m) => m.action_items_open || 0,
      render: (m) => {
        const total = m.action_items_total || 0;
        const open = m.action_items_open || 0;
        if (total === 0) return <span className="text-[11px] text-muted-foreground">—</span>;
        const tone = open === 0 ? "bg-emerald-50 border-emerald-300 text-emerald-700" : open === total ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-amber-50 border-amber-300 text-amber-700";
        return <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border ${tone}`}>{open}/{total} open</span>;
      },
    },
    {
      key: "mode", label: "Mode", width: 130, sortable: true, groupable: true,
      filterType: "select",
      filterOptions: COMM_MODES.map((m) => ({ value: m.value, label: m.label })),
      accessor: (m) => m.mode || "video_call",
      render: (m) => {
        const cfg = MODE_BY_VALUE[m.mode || "video_call"] || MODE_BY_VALUE.video_call;
        const Icon = cfg.icon;
        return (
          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border"
                 style={{ borderColor: cfg.color, color: cfg.color }}>
            <Icon className="w-3 h-3" /> {cfg.label}
          </span>
        );
      },
    },
    {
      key: "scope", label: "Scope", width: 110, sortable: true, groupable: true,
      filterType: "select", filterOptions: [{ value: "internal", label: "Internal" }, { value: "external", label: "External" }],
      accessor: (m) => m.is_external ? "external" : "internal",
      render: (m) => m.is_external
        ? <span className="chip chip-client inline-flex items-center gap-1 text-[10px]"><Globe className="w-3 h-3" /> external</span>
        : <span className="chip chip-approved inline-flex items-center gap-1 text-[10px]"><Lock className="w-3 h-3" /> internal</span>,
    },
    {
      key: "location", label: "Where", width: 140, sortable: true, filterType: "text", visible: false,
      accessor: (m) => m.location || m.video_url || "",
      render: (m) => <span className="text-xs truncate">{m.location || m.video_url || "—"}</span>,
    },
    {
      key: "rating", label: "Rating", width: 90, sortable: true, visible: false,
      accessor: (m) => m.rating || 0,
      render: (m) => (typeof m.rating === "number" && m.rating > 0)
        ? <span className="inline-flex items-center gap-0.5 text-amber-600 text-xs"><Star className="w-3 h-3 fill-current" /> {m.rating}</span>
        : <span className="text-[11px] text-muted-foreground">—</span>,
    },
    {
      key: "status", label: "Status", width: 110, sortable: true, groupable: true,
      filterType: "select", filterOptions: [
        { value: "scheduled", label: "scheduled" },
        { value: "in_progress", label: "in progress" },
        { value: "completed", label: "completed" },
        { value: "cancelled", label: "cancelled" },
      ],
      accessor: (m) => m.status || "scheduled",
      render: (m) => <span className="chip text-[10px]">{(m.status || "scheduled").replace("_", " ")}</span>,
    },
    {
      key: "actions", label: "Actions", width: 190,
      accessor: () => 0,
      render: (m) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" className="h-6 px-1 text-[10px] rounded-none"
                  title="Quick preview — all details without leaving this page"
                  onClick={() => setPreviewing(m)}
                  data-testid={`meeting-preview-${m.id}`}>
            <Eye className="w-3 h-3 mr-0.5" /> Preview
          </Button>
          <Button size="sm" variant="ghost" className="h-6 px-1 text-[10px] rounded-none"
                  title="Reschedule"
                  onClick={() => setRescheduleTarget(m)}
                  data-testid={`meeting-reschedule-${m.id}`}>
            <CalendarClock className="w-3 h-3 mr-0.5" /> Move
          </Button>
          <Button size="sm" variant="ghost" className="h-6 px-1 text-[10px] rounded-none"
                  title={typeof m.rating === "number" && m.rating > 0 ? `Rated ${m.rating}/5` : "Rate meeting"}
                  onClick={() => setRateTarget(m)}
                  data-testid={`meeting-rate-${m.id}`}>
            <Star className={`w-3 h-3 mr-0.5 ${typeof m.rating === "number" && m.rating > 0 ? "text-amber-500 fill-current" : ""}`} /> Rate
          </Button>
        </div>
      ),
    },
  ]), []);

  return (
    <>
      <PageHeader
        eyebrow="Comms"
        title="Meetings & Comms"
        description="Every business interaction — meetings, calls, chats, emails, in-person visits — logged in one place. Filter by mode, project, or scope; your view is saved per user."
        testid="all-meetings-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setEditing({ __logged: true })} data-testid="meetings-log-btn">
              <Plus className="w-4 h-4 mr-2" /> Log past interaction
            </Button>
            <Button className="rounded-none" onClick={() => setEditing({})} data-testid="meetings-new-btn">
              <Plus className="w-4 h-4 mr-2" /> New meeting
            </Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 space-y-2 pt-3">
        <div className="border border-border bg-muted/40 p-2 flex flex-wrap items-center gap-2" data-testid="all-meetings-filters">
          <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or description…" className="h-8 w-56 text-xs rounded-none" data-testid="all-meetings-q" />
          <FastMultiSelect
            options={clientOptions}
            value={clientIds}
            onChange={setClientIds}
            placeholder="All clients"
            label="Filter by client"
            width="w-40"
            testid="all-meetings-client"
          />
          <FastMultiSelect
            options={projectOptions}
            value={projectIds}
            onChange={setProjectIds}
            placeholder="All projects"
            label="Filter by project"
            width="w-40"
            testid="all-meetings-project"
          />
          <Select value={modeFilter || "_all"} onValueChange={(v) => setModeFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-36 text-xs rounded-none" data-testid="all-meetings-mode"><SelectValue placeholder="All modes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All modes</SelectItem>
              {COMM_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scope || "_all"} onValueChange={(v) => setScope(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-32 text-xs rounded-none" data-testid="all-meetings-scope"><SelectValue placeholder="Int + ext" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Internal + ext.</SelectItem>
              <SelectItem value="internal">Internal only</SelectItem>
              <SelectItem value="external">External only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={loggedFilter || "_all"} onValueChange={(v) => setLoggedFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-36 text-xs rounded-none" data-testid="all-meetings-logged"><SelectValue placeholder="Live + logged" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Live + logged</SelectItem>
              <SelectItem value="false">Live only</SelectItem>
              <SelectItem value="true">Logged (past) only</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[10px] text-muted-foreground ml-auto whitespace-nowrap" data-testid="all-meetings-count">{filteredRows.length} of {rows.length}</span>
        </div>
        <div className="border border-border bg-background p-2 flex flex-wrap items-center gap-1" data-testid="all-meetings-date-toggles">
          <span className="text-[10px] text-muted-foreground mr-1 uppercase tracking-wide">Range</span>
          {[
            { key: "past", label: "Past" },
            { key: "today", label: "Today" },
            { key: "recent", label: "Recent (7d)" },
            { key: "upcoming", label: "Upcoming (7d)" },
            { key: "this_month", label: "This month" },
            { key: "next_month", label: "Next month" },
            { key: "all", label: "All" },
          ].map((t) => (
            <Button
              key={t.key}
              size="sm"
              variant={timeRange === t.key ? "default" : "outline"}
              onClick={() => setTimeRange(t.key)}
              className="rounded-none h-7 text-[11px] px-2"
              data-testid={`all-meetings-range-${t.key}`}
            >
              {t.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="px-4 sm:px-6 md:px-10 py-4">
        <AdvancedTable
          tableKey="meetings"
          title="Meetings"
          rows={filteredRows}
          columns={columns}
          loading={busy && rows.length === 0}
          emptyIcon={Calendar}
          emptyTitle="No meetings found"
          emptyDescription="Try clearing filters, adjusting the range, or schedule a meeting from a project's Meetings tab."
          onRowClick={(m) => nav(`/app/meetings/${m.id}`)}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          renderBulkBar={() => (
            <>
              <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setReassignOpen(true)} data-testid="meetings-bulk-reassign">
                Reassign owner
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 border-destructive/50 text-destructive" onClick={bulkDelete} data-testid="meetings-bulk-delete">
                Delete
              </Button>
            </>
          )}
        />
      </div>
      {editing && (
        <MeetingEditor
          meeting={editing}
          projects={projects}
          workspaceMembers={members}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {reassignOpen && (
        <ReassignDialog
          members={members}
          count={selectedIds.size}
          onCancel={() => setReassignOpen(false)}
          onConfirm={bulkReassign}
        />
      )}
      {rescheduleTarget && (
        <RescheduleDialog
          meeting={rescheduleTarget}
          onCancel={() => setRescheduleTarget(null)}
          onDone={() => { setRescheduleTarget(null); load(); }}
        />
      )}
      {rateTarget && (
        <RateMeetingDialog
          meeting={rateTarget}
          onCancel={() => setRateTarget(null)}
          onDone={() => { setRateTarget(null); load(); }}
        />
      )}
      {previewing && (
        <MeetingPreviewDialog
          meeting={previewing}
          onClose={() => setPreviewing(null)}
          onOpenFull={(id) => { setPreviewing(null); nav(`/app/meetings/${id}`); }}
        />
      )}
    </>
  );
}
function ReassignDialog({ members, count, onCancel, onConfirm }) {
  const [ownerId, setOwnerId] = useState("");
  const active = (members || []).filter((m) => (m.status || "active") !== "removed" && (m.status || "active") !== "deleted");
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="meetings-reassign-dialog">
        <DialogHeader>
          <DialogTitle>Reassign {count} meeting{count === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Change the organizer (created_by) on every selected meeting. Meetings you cannot edit are silently skipped.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="label-eyebrow text-[10px]">New organizer</Label>
          <Select value={ownerId} onValueChange={setOwnerId}>
            <SelectTrigger className="rounded-none" data-testid="meetings-reassign-select"><SelectValue placeholder="— select a workspace member —" /></SelectTrigger>
            <SelectContent>
              {active.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.name || m.email || m.user_id?.slice(0, 8)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button className="rounded-none" disabled={!ownerId} onClick={() => onConfirm(ownerId)} data-testid="meetings-reassign-confirm">Reassign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function RescheduleDialog({ meeting, onCancel, onDone }) {
  const [when, setWhen] = useState(() => {
    try { return new Date(meeting.scheduled_at).toISOString().slice(0, 16); }
    catch { return new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16); }
  });
  const [duration, setDuration] = useState(meeting.duration_minutes || 30);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!when) return;
    setBusy(true);
    try {
      const iso = new Date(when).toISOString();
      await api.post(`/meetings/${meeting.id}/reschedule`, {
        scheduled_at: iso,
        duration_minutes: Number(duration) || 30,
        reason: reason || null,
      });
      toast.success("Meeting rescheduled — attendees notified");
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reschedule failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md rounded-none" data-testid="meeting-reschedule-dialog">
        <DialogHeader>
          <DialogTitle>Reschedule meeting</DialogTitle>
          <DialogDescription className="truncate">{meeting.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px]">New date & time</Label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-9 rounded-none" data-testid="meeting-reschedule-when" />
          </div>
          <div>
            <Label className="label-eyebrow text-[10px]">Duration (minutes)</Label>
            <Input type="number" min={5} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} className="h-9 rounded-none" data-testid="meeting-reschedule-duration" />
          </div>
          <div>
            <Label className="label-eyebrow text-[10px]">Reason (optional — visible to attendees)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="rounded-none" data-testid="meeting-reschedule-reason" placeholder="e.g. Client requested a later slot" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button className="rounded-none" disabled={busy || !when} onClick={submit} data-testid="meeting-reschedule-confirm">
            {busy ? "Rescheduling…" : "Reschedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RateMeetingDialog({ meeting, onCancel, onDone }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!stars) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/rate`, { rating: stars, comment: comment || null });
      toast.success(`Rating saved · average ${data.avg}/5`);
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Rating failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md rounded-none" data-testid="meeting-rate-dialog">
        <DialogHeader>
          <DialogTitle>Rate this meeting</DialogTitle>
          <DialogDescription className="truncate">{meeting.title} · Ratings ≥ 4★ award +5 leaderboard points to every attendee.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-1" data-testid="meeting-rate-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setStars(n)}
                className="p-1"
                data-testid={`meeting-rate-star-${n}`}
                title={`${n} star${n === 1 ? "" : "s"}`}
              >
                <Star className={`w-7 h-7 ${n <= stars ? "text-amber-500 fill-current" : "text-muted-foreground"}`} />
              </button>
            ))}
            <span className="text-xs text-muted-foreground ml-2">{stars ? `${stars}/5` : "Tap a star"}</span>
          </div>
          <div>
            <Label className="label-eyebrow text-[10px]">Comment (optional)</Label>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="rounded-none" data-testid="meeting-rate-comment" placeholder="What made this meeting productive or unproductive?" />
          </div>
          {typeof meeting.rating === "number" && meeting.rating > 0 && (
            <div className="text-[11px] text-muted-foreground">
              Current average: <span className="text-amber-600">{meeting.rating}/5</span> across {meeting.rating_count || 0} rating{meeting.rating_count === 1 ? "" : "s"}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button className="rounded-none" disabled={busy || !stars} onClick={submit} data-testid="meeting-rate-confirm">
            {busy ? "Saving…" : "Save rating"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function MeetingEditor({ meeting, projects, workspaceMembers = [], onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const isNew = !meeting?.id;
  const isLoggedIntent = !!meeting?.__logged;   // Log Past Interaction entry point
  // Iter 253p · Series editor drawer state (only meaningful when editing an
  // existing meeting that belongs to a recurring series).
  const [seriesEditorOpen, setSeriesEditorOpen] = useState(false);
  const [form, setForm] = useState(() => ({
    project_id: meeting?.project_id || "",
    title: meeting?.title || "",
    description: meeting?.description || "",
    scheduled_at: meeting?.scheduled_at
      ? meeting.scheduled_at.slice(0, 16)
      : (isLoggedIntent
          ? new Date().toISOString().slice(0, 16)
          : new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16)),
    duration_minutes: meeting?.duration_minutes || 30,
    location: meeting?.location || "",
    video_url: meeting?.video_url || "",
    is_external: meeting?.is_external || false,
    discussion_notes: meeting?.discussion_notes || "",
    // Iter 253k · Mode + per-mode fields.
    mode: meeting?.mode || "video_call",
    is_logged: meeting?.is_logged ?? isLoggedIntent,
    phone_number: meeting?.phone_number || "",
    call_duration_seconds: meeting?.call_duration_seconds || "",
    recording_url: meeting?.recording_url || "",
    call_outcome: meeting?.call_outcome || "",
    video_platform: meeting?.video_platform || "",
    address: meeting?.address || "",
    location_lat: meeting?.location_lat || "",
    location_lng: meeting?.location_lng || "",
    email_subject: meeting?.email_subject || "",
    email_message_count: meeting?.email_message_count || "",
    chat_platform: meeting?.chat_platform || "",
    chat_channel: meeting?.chat_channel || "",
    chat_link: meeting?.chat_link || "",
    // R148h · Feb 2026 — Private meeting toggle. When ON, only the host,
    // invited internal attendees and workspace admins can see this row.
    is_private: !!meeting?.is_private,
    attendee_user_ids: meeting?.attendee_user_ids || [],
    external_attendees: meeting?.external_attendees || [],
    // Iter 253o · Restored agenda + recurrence in the editor. Existing
    // meetings surface their saved agenda; new meetings start empty and
    // grow via the add button below.
    agenda: (meeting?.agenda || []).map((a) => ({
      id: a.id, title: a.title || "", description: a.description || "",
      duration_minutes: a.duration_minutes || "",
      status: a.status || "pending",
    })),
    // Recurrence rule — surfaced only for scheduled meetings, hidden when
    // logging a past interaction (recurrence makes no sense retroactively).
    recurrence_enabled: !!meeting?.recurrence_rule,
    rec_frequency: meeting?.recurrence_rule?.frequency || "weekly",
    rec_interval: meeting?.recurrence_rule?.interval || 1,
    rec_weekdays: meeting?.recurrence_rule?.weekdays || [],
    rec_occurrences: meeting?.recurrence_rule?.occurrences || 12,
    rec_until_date: meeting?.recurrence_rule?.until_date || "",
  }));
  const [busy, setBusy] = useState(false);
  // Iter 253l · Load project members for the attendee picker as soon as
  // a project is chosen (or on mount for existing meetings).
  const [projectMembers, setProjectMembers] = useState([]);
  const [extName, setExtName] = useState("");
  const [extEmail, setExtEmail] = useState("");
  useEffect(() => {
    const pid = meeting?.project_id || form.project_id;
    if (!pid) { setProjectMembers([]); return; }
    let cancel = false;
    api.get(`/projects/${pid}/members`)
      .then((r) => { if (!cancel) setProjectMembers(r.data || []); })
      .catch(() => { if (!cancel) setProjectMembers([]); });
    return () => { cancel = true; };
  }, [meeting?.project_id, form.project_id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      // Base payload shared across all modes.
      const payload = {
        title: form.title.trim(),
        description: form.description || null,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 30,
        location: form.location || null,
        video_url: form.video_url || null,
        discussion_notes: form.discussion_notes || null,
        mode: form.mode,
        is_logged: !!form.is_logged,
      };
      // Attach mode-specific fields.
      const m = form.mode;
      if (m === "audio_call" || m === "video_call") {
        if (form.recording_url) payload.recording_url = form.recording_url;
        if (form.call_duration_seconds) payload.call_duration_seconds = Number(form.call_duration_seconds);
      }
      if (m === "audio_call" || m === "whatsapp") {
        if (form.phone_number) payload.phone_number = form.phone_number;
      }
      if (m === "audio_call") {
        if (form.call_outcome) payload.call_outcome = form.call_outcome;
      }
      if (m === "video_call") {
        if (form.video_platform) payload.video_platform = form.video_platform;
      }
      if (m === "in_person" || m === "site_visit") {
        if (form.address) payload.address = form.address;
        if (form.location_lat) payload.location_lat = Number(form.location_lat);
        if (form.location_lng) payload.location_lng = Number(form.location_lng);
      }
      if (m === "email_thread") {
        if (form.email_subject) payload.email_subject = form.email_subject;
        if (form.email_message_count) payload.email_message_count = Number(form.email_message_count);
      }
      if (m === "chat_dm" || m === "whatsapp") {
        if (form.chat_platform) payload.chat_platform = form.chat_platform;
        if (form.chat_channel) payload.chat_channel = form.chat_channel;
        if (form.chat_link) payload.chat_link = form.chat_link;
      }
      if (isNew) {
        // Iter 253o · Only send recurrence for real scheduled meetings.
        // Past-interaction logs (is_logged) skip it entirely.
        const rec = form.recurrence_enabled && !form.is_logged
          ? {
              frequency: form.rec_frequency,
              interval: Number(form.rec_interval) || 1,
              occurrences: Number(form.rec_occurrences) || 12,
              ...(form.rec_frequency === "weekly" && form.rec_weekdays.length
                ? { weekdays: form.rec_weekdays } : {}),
              ...(form.rec_until_date
                ? { until_date: form.rec_until_date } : {}),
            }
          : null;
        const agendaClean = (form.agenda || [])
          .filter((a) => (a.title || "").trim())
          .map((a) => ({
            id: a.id || undefined,
            title: a.title.trim(),
            description: a.description || null,
            duration_minutes: a.duration_minutes ? Number(a.duration_minutes) : null,
            status: a.status || "pending",
          }));
        // Feb 2026 R293d · When no project is picked, meetings route
        // through the new workspace-scoped endpoint. Otherwise use the
        // existing project-scoped endpoint which inherits perms + feeds
        // the project tab.
        const createUrl = form.project_id
          ? `/projects/${form.project_id}/meetings`
          : `/workspaces/${activeWorkspaceId}/meetings`;
        await api.post(createUrl, {
          ...payload, is_external: !!form.is_external,
          is_private: !!form.is_private,
          attendee_user_ids: form.attendee_user_ids || [],
          external_attendees: form.external_attendees || [],
          agenda: agendaClean,
          ...(rec ? { recurrence: rec } : {}),
        });
        toast.success(form.is_logged ? "Interaction logged" : (rec ? "Recurring meeting scheduled" : "Meeting scheduled"));
      } else {
        const agendaClean = (form.agenda || [])
          .filter((a) => (a.title || "").trim())
          .map((a) => ({
            id: a.id || undefined,
            title: a.title.trim(),
            description: a.description || null,
            duration_minutes: a.duration_minutes ? Number(a.duration_minutes) : null,
            status: a.status || "pending",
          }));
        await api.patch(`/meetings/${meeting.id}`, {
          ...payload,
          is_private: !!form.is_private,
          attendee_user_ids: form.attendee_user_ids || [],
          external_attendees: form.external_attendees || [],
          agenda: agendaClean,
        });
        toast.success("Interaction updated");
      }
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save interaction"));
    } finally { setBusy(false); }
  };

  const modeCfg = MODE_BY_VALUE[form.mode] || MODE_BY_VALUE.video_call;
  const showLocation = form.mode === "in_person" || form.mode === "site_visit";
  const showVideoUrl = form.mode === "video_call";
  const showPhone = form.mode === "audio_call" || form.mode === "whatsapp";
  const showRecording = form.mode === "audio_call" || form.mode === "video_call";
  const showEmailFields = form.mode === "email_thread";
  const showChatFields = form.mode === "chat_dm" || form.mode === "whatsapp";

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="meeting-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? (isLoggedIntent ? "Log past interaction" : "New interaction") : "Edit interaction"}</DialogTitle>
          <DialogDescription>
            Capture any business interaction — meetings, calls, chats, emails, in-person visits. Extra fields appear based on the mode you select.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Iter 253o · Meeting vs Interaction segmented toggle. This is the
              primary discriminator: scheduled meeting (invites, calendar,
              recurrence) vs logging a past interaction (no invites, marked
              done immediately). */}
          {isNew && (
            <div className="inline-flex border border-border overflow-hidden w-full" data-testid="meeting-editor-mode-toggle">
              <button
                type="button"
                onClick={() => { update("is_logged", false); }}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition ${!form.is_logged ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                data-testid="meeting-editor-toggle-scheduled"
              >
                <Calendar className="w-3 h-3 inline mr-1.5" /> Schedule meeting
              </button>
              <button
                type="button"
                onClick={() => { update("is_logged", true); update("recurrence_enabled", false); }}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition border-l border-border ${form.is_logged ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                data-testid="meeting-editor-toggle-logged"
              >
                <MessageSquare className="w-3 h-3 inline mr-1.5" /> Log past interaction
              </button>
            </div>
          )}
          {isNew && (
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Project <span className="text-muted-foreground font-normal">(optional)</span></Label>
              {/* Feb 2026 R293d · Sorted + searchable project picker.
                  Project is optional for ad-hoc / internal meetings. */}
              <SearchableSelect
                value={form.project_id || ""}
                onValueChange={(v) => update("project_id", v || "")}
                clearable
                width="100%"
                placeholder="Pick a project (optional)"
                triggerClassName="h-10 rounded-none"
                testid="meeting-editor-project"
                options={[...projects]
                  .sort((a, b) => (a.name || "").toLowerCase()
                                      .localeCompare((b.name || "").toLowerCase()))
                  .map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>
          )}
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Mode of communication</Label>
            <Select value={form.mode} onValueChange={(v) => update("mode", v)}>
              <SelectTrigger className="h-10 rounded-none" data-testid="meeting-editor-mode">
                <SelectValue>
                  <span className="inline-flex items-center gap-2">
                    <modeCfg.icon className="w-3.5 h-3.5" style={{ color: modeCfg.color }} />
                    {modeCfg.label}
                  </span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {COMM_MODES.map((m) => {
                  const Icon = m.icon;
                  return (
                    <SelectItem key={m.value} value={m.value} data-testid={`meeting-editor-mode-${m.value}`}>
                      <span className="inline-flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" style={{ color: m.color }} /> {m.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Title</Label>
            <Input value={form.title} onChange={(e) => update("title", e.target.value)} className="h-10 rounded-none" data-testid="meeting-editor-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">
                {form.is_logged ? "When it happened" : "When"}
              </Label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => update("scheduled_at", e.target.value)} className="h-10 rounded-none" data-testid="meeting-editor-when" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Duration (min)</Label>
              <Input type="number" min={0} max={600} value={form.duration_minutes} onChange={(e) => update("duration_minutes", e.target.value)} className="h-10 rounded-none" data-testid="meeting-editor-duration" />
            </div>
          </div>
          {/* Mode-specific fields */}
          {showVideoUrl && (
            <div className="grid grid-cols-2 gap-3" data-testid="meeting-editor-video-block">
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Meeting link</Label>
                <Input value={form.video_url} onChange={(e) => update("video_url", e.target.value)} className="h-10 rounded-none" placeholder="https://…" data-testid="meeting-editor-video" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Platform</Label>
                <Select value={form.video_platform || "_none"} onValueChange={(v) => update("video_platform", v === "_none" ? "" : v)}>
                  <SelectTrigger className="h-10 rounded-none" data-testid="meeting-editor-video-platform"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    <SelectItem value="zoom">Zoom</SelectItem>
                    <SelectItem value="google_meet">Google Meet</SelectItem>
                    <SelectItem value="teams">Microsoft Teams</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {showPhone && (
            <div className="grid grid-cols-2 gap-3" data-testid="meeting-editor-phone-block">
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Phone number</Label>
                <Input value={form.phone_number} onChange={(e) => update("phone_number", e.target.value)} className="h-10 rounded-none" placeholder="+91 98…" data-testid="meeting-editor-phone" />
              </div>
              {form.mode === "audio_call" && (
                <div>
                  <Label className="label-eyebrow mb-1.5 inline-block">Call outcome</Label>
                  <Select value={form.call_outcome || "_none"} onValueChange={(v) => update("call_outcome", v === "_none" ? "" : v)}>
                    <SelectTrigger className="h-10 rounded-none" data-testid="meeting-editor-call-outcome"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">—</SelectItem>
                      <SelectItem value="connected">Connected</SelectItem>
                      <SelectItem value="no_answer">No answer</SelectItem>
                      <SelectItem value="voicemail">Voicemail</SelectItem>
                      <SelectItem value="busy">Busy</SelectItem>
                      <SelectItem value="wrong_number">Wrong number</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          {showRecording && (
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Recording URL</Label>
              <Input value={form.recording_url} onChange={(e) => update("recording_url", e.target.value)} className="h-10 rounded-none" placeholder="https://…" data-testid="meeting-editor-recording" />
            </div>
          )}
          {showLocation && (
            <div className="space-y-3" data-testid="meeting-editor-location-block">
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Location / address</Label>
                <Input value={form.location || form.address} onChange={(e) => { update("location", e.target.value); update("address", e.target.value); }} className="h-10 rounded-none" placeholder="Office / Client site" data-testid="meeting-editor-location" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input value={form.location_lat} onChange={(e) => update("location_lat", e.target.value)} className="h-10 rounded-none" placeholder="Lat (optional)" data-testid="meeting-editor-lat" />
                <Input value={form.location_lng} onChange={(e) => update("location_lng", e.target.value)} className="h-10 rounded-none" placeholder="Lng (optional)" data-testid="meeting-editor-lng" />
              </div>
            </div>
          )}
          {showEmailFields && (
            <div className="grid grid-cols-2 gap-3" data-testid="meeting-editor-email-block">
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Email thread subject</Label>
                <Input value={form.email_subject} onChange={(e) => update("email_subject", e.target.value)} className="h-10 rounded-none" data-testid="meeting-editor-email-subject" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Messages in thread</Label>
                <Input type="number" min={0} value={form.email_message_count} onChange={(e) => update("email_message_count", e.target.value)} className="h-10 rounded-none" data-testid="meeting-editor-email-count" />
              </div>
            </div>
          )}
          {showChatFields && (
            <div className="space-y-3" data-testid="meeting-editor-chat-block">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-eyebrow mb-1.5 inline-block">Chat platform</Label>
                  <Select value={form.chat_platform || "_none"} onValueChange={(v) => update("chat_platform", v === "_none" ? "" : v)}>
                    <SelectTrigger className="h-10 rounded-none" data-testid="meeting-editor-chat-platform"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">—</SelectItem>
                      <SelectItem value="slack">Slack</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="teams">Teams</SelectItem>
                      <SelectItem value="telegram">Telegram</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow mb-1.5 inline-block">Channel / group</Label>
                  <Input value={form.chat_channel} onChange={(e) => update("chat_channel", e.target.value)} className="h-10 rounded-none" data-testid="meeting-editor-chat-channel" />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Chat link</Label>
                <Input value={form.chat_link} onChange={(e) => update("chat_link", e.target.value)} className="h-10 rounded-none" placeholder="https://…" data-testid="meeting-editor-chat-link" />
              </div>
            </div>
          )}
          {/* Iter 253l · Attendee picker — internal users + external contacts
              Feb 2026 · Replaced chip grid with sorted + searchable multi-select
              so the edit flow matches the create flow and large rosters stay fast. */}
          <div className="border border-border p-3 bg-muted/10" data-testid="meeting-editor-attendees-block">
            <div className="label-eyebrow mb-1.5 inline-flex items-center gap-1"><Users className="w-3 h-3" /> Internal attendees</div>
            {(() => {
              // Prefer project members when a project is set; fall back to the
              // full workspace roster (meetings can be workspace-scoped).
              const srcRaw = projectMembers.length ? projectMembers : workspaceMembers;
              const src = (srcRaw || []).filter((m) => (m.status || "active") !== "removed" && (m.status || "active") !== "deleted");
              const seen = new Set();
              const opts = [];
              src.forEach((m) => {
                if (!m.user_id || seen.has(m.user_id)) return;
                seen.add(m.user_id);
                opts.push({
                  value: m.user_id,
                  label: m.user?.name || m.user?.email || "User",
                  testid: `meeting-editor-attendee-${m.user_id}`,
                });
              });
              return (
                <MultiSelect
                  options={opts}
                  value={form.attendee_user_ids || []}
                  onChange={(next) => update("attendee_user_ids", next)}
                  placeholder={opts.length ? "Add internal attendees…" : "Pick a project or add workspace members first"}
                  allLabel="Add internal attendees"
                  width="w-full"
                  disabled={!opts.length}
                  testid="meeting-editor-attendees-multi"
                />
              );
            })()}
            <div className="pt-2 mt-2 border-t border-dashed border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">External contacts</div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(form.external_attendees || []).map((ext, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border"
                        data-testid={`meeting-editor-ext-chip-${idx}`}>
                    {ext.name || ext.email}
                    <button type="button" onClick={() => update("external_attendees",
                      form.external_attendees.filter((_, i) => i !== idx))}
                      className="hover:text-red-600"
                      data-testid={`meeting-editor-ext-remove-${idx}`}>
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                <Input value={extName || ""} onChange={(e) => setExtName(e.target.value)}
                       placeholder="Name" className="h-8 text-xs col-span-2"
                       data-testid="meeting-editor-ext-name" />
                <Input value={extEmail || ""} onChange={(e) => setExtEmail(e.target.value)}
                       placeholder="Email" className="h-8 text-xs col-span-2"
                       data-testid="meeting-editor-ext-email" />
                <Button type="button" variant="outline" className="rounded-none h-8 text-xs"
                        onClick={() => {
                          const nm = (extName || "").trim();
                          const em = (extEmail || "").trim();
                          if (!nm && !em) return;
                          update("external_attendees",
                            [...(form.external_attendees || []), { name: nm, email: em }]);
                          setExtName(""); setExtEmail("");
                        }}
                        data-testid="meeting-editor-ext-add">
                  Add
                </Button>
              </div>
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Description / discussion notes</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} className="rounded-none" data-testid="meeting-editor-description" />
          </div>

          {/* Iter 253o · Agenda block — restored so users can seed agenda at
              creation time. Available in every mode; on the detail page each
              item can be marked discussed/skipped. */}
          <div className="border border-border p-3 bg-muted/10" data-testid="meeting-editor-agenda-block">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Agenda ({(form.agenda || []).length})
              </div>
              <Button type="button" variant="outline" size="sm"
                onClick={() => update("agenda", [...(form.agenda || []), { id: null, title: "", description: "", duration_minutes: "", status: "pending" }])}
                className="rounded-none h-7 text-[10px]" data-testid="meeting-editor-agenda-add">
                <Plus className="w-3 h-3 mr-1" /> Add item
              </Button>
            </div>
            {(form.agenda || []).length === 0 && (
              <p className="text-[11px] text-muted-foreground italic">No agenda yet. Add discussion points, decisions to make, or updates to review.</p>
            )}
            <div className="space-y-2">
              {(form.agenda || []).map((a, idx) => (
                <div key={idx} className="flex items-start gap-2 border border-border bg-background p-2" data-testid={`meeting-editor-agenda-row-${idx}`}>
                  <span className="text-[10px] text-muted-foreground pt-2 w-4 text-center">{idx + 1}.</span>
                  <div className="flex-1 space-y-1">
                    <Input
                      value={a.title || ""}
                      onChange={(e) => {
                        const next = [...form.agenda];
                        next[idx] = { ...next[idx], title: e.target.value };
                        update("agenda", next);
                      }}
                      placeholder="Item title (e.g. Review Q1 KPIs)"
                      className="h-7 text-xs rounded-none"
                      data-testid={`meeting-editor-agenda-title-${idx}`}
                    />
                    <div className="grid grid-cols-4 gap-1">
                      <Input
                        value={a.description || ""}
                        onChange={(e) => {
                          const next = [...form.agenda];
                          next[idx] = { ...next[idx], description: e.target.value };
                          update("agenda", next);
                        }}
                        placeholder="Notes (optional)"
                        className="h-7 text-[11px] rounded-none col-span-2"
                        data-testid={`meeting-editor-agenda-desc-${idx}`}
                      />
                      <Input
                        type="number"
                        min={0}
                        value={a.duration_minutes || ""}
                        onChange={(e) => {
                          const next = [...form.agenda];
                          next[idx] = { ...next[idx], duration_minutes: e.target.value };
                          update("agenda", next);
                        }}
                        placeholder="Min"
                        className="h-7 text-[11px] rounded-none"
                        data-testid={`meeting-editor-agenda-mins-${idx}`}
                      />
                      <Select value={a.status || "pending"}
                              onValueChange={(v) => {
                                const next = [...form.agenda];
                                next[idx] = { ...next[idx], status: v };
                                update("agenda", next);
                              }}>
                        <SelectTrigger className="h-7 text-[11px] rounded-none" data-testid={`meeting-editor-agenda-status-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="discussed">Discussed</SelectItem>
                          <SelectItem value="skipped">Skipped</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-rose-600 p-1"
                    onClick={() => update("agenda", form.agenda.filter((_, i) => i !== idx))}
                    data-testid={`meeting-editor-agenda-remove-${idx}`}
                    aria-label="Remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            {!isNew && (
              <p className="text-[10px] text-muted-foreground mt-2 italic">
                Action items and per-item discussion notes are managed on the meeting detail page.
              </p>
            )}
          </div>

          {/* Iter 253o · Recurrence block — only for scheduled meetings.
              Hidden entirely when the user is logging a past interaction. */}
          {isNew && !form.is_logged && (
            <div className="border border-border p-3 bg-muted/10" data-testid="meeting-editor-recurrence-block">
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input type="checkbox"
                       checked={form.recurrence_enabled}
                       onChange={(e) => update("recurrence_enabled", e.target.checked)}
                       data-testid="meeting-editor-recurrence-toggle" />
                <span className="label-eyebrow inline-flex items-center gap-1">
                  <Repeat className="w-3 h-3" /> Repeat this meeting
                </span>
              </label>
              {form.recurrence_enabled && (
                <div className="space-y-2 pl-6">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Frequency</Label>
                      <Select value={form.rec_frequency} onValueChange={(v) => update("rec_frequency", v)}>
                        <SelectTrigger className="h-8 text-xs rounded-none" data-testid="meeting-editor-rec-freq">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="yearly">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Every</Label>
                      <Input type="number" min={1} max={52}
                             value={form.rec_interval}
                             onChange={(e) => update("rec_interval", e.target.value)}
                             className="h-8 text-xs rounded-none"
                             data-testid="meeting-editor-rec-interval" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Max occurrences</Label>
                      <Input type="number" min={1} max={104}
                             value={form.rec_occurrences}
                             onChange={(e) => update("rec_occurrences", e.target.value)}
                             className="h-8 text-xs rounded-none"
                             data-testid="meeting-editor-rec-occurrences" />
                    </div>
                  </div>
                  {form.rec_frequency === "weekly" && (
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">On days</Label>
                      <div className="flex gap-1 mt-1" data-testid="meeting-editor-rec-weekdays">
                        {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map((d) => {
                          const on = form.rec_weekdays.includes(d);
                          return (
                            <button
                              key={d}
                              type="button"
                              onClick={() => update("rec_weekdays",
                                on
                                  ? form.rec_weekdays.filter((x) => x !== d)
                                  : [...form.rec_weekdays, d])}
                              className={`text-[10px] w-8 h-7 border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                              data-testid={`meeting-editor-rec-weekday-${d}`}
                            >
                              {d}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1 italic">
                        Leave blank to use the day of the meeting's start date.
                      </p>
                    </div>
                  )}
                  <div>
                    <Label className="text-[10px] uppercase text-muted-foreground">Ends on (optional)</Label>
                    <Input type="date"
                           value={form.rec_until_date}
                           onChange={(e) => update("rec_until_date", e.target.value)}
                           className="h-8 text-xs rounded-none w-48"
                           data-testid="meeting-editor-rec-until" />
                  </div>
                  <p className="text-[11px] text-muted-foreground italic pt-1">
                    Occurrences are pre-created up to Max, then the next one auto-spawns each time an occurrence is marked done.
                  </p>
                </div>
              )}
            </div>
          )}

          {isNew && (
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.is_external} onChange={(e) => update("is_external", e.target.checked)} data-testid="meeting-editor-external" />
                External interaction (clients can see)
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.is_logged} onChange={(e) => { update("is_logged", e.target.checked); if (e.target.checked) update("recurrence_enabled", false); }} data-testid="meeting-editor-logged" />
                Log as past interaction (no invites sent)
              </label>
              <label className="flex items-center gap-2 text-xs" title="Private meetings are only visible to invited internal attendees + workspace admins.">
                <input type="checkbox" checked={form.is_private} onChange={(e) => update("is_private", e.target.checked)} data-testid="meeting-editor-private" />
                <EyeOff className="w-3 h-3 text-purple-600" /> Private (attendees only)
              </label>
            </div>
          )}
          {!isNew && (
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-xs" title="Private meetings are only visible to invited internal attendees + workspace admins.">
                <input type="checkbox" checked={form.is_private} onChange={(e) => update("is_private", e.target.checked)} data-testid="meeting-editor-private" />
                <EyeOff className="w-3 h-3 text-purple-600" /> Private (attendees only)
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          {!isNew && meeting?.series_id && (
            <Button
              variant="outline"
              className="rounded-none mr-auto"
              onClick={() => setSeriesEditorOpen(true)}
              data-testid="meeting-editor-open-series"
              title="Edit this meeting's whole recurring series at once"
            >
              <Repeat className="w-3.5 h-3.5 mr-1" /> Edit series…
            </Button>
          )}
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="meeting-editor-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="meeting-editor-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
      {seriesEditorOpen && (
        <SeriesEditorDialog
          meeting={meeting}
          onClose={() => setSeriesEditorOpen(false)}
          onSaved={() => { setSeriesEditorOpen(false); if (onSaved) onSaved(); }}
        />
      )}
    </Dialog>
  );
}


// ─────── Iter 253p · Recurring Series bulk-editor ───────

export function SeriesEditorDialog({ meeting, onClose, onSaved }) {
  const [scope, setScope] = useState("future");
  const [fromMeetingId, setFromMeetingId] = useState(meeting.id);
  const [shiftMinutes, setShiftMinutes] = useState(0);
  const [duration, setDuration] = useState("");
  const [mode, setMode] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [location, setLocation] = useState("");
  const [replaceAgenda, setReplaceAgenda] = useState(false);
  const [agenda, setAgenda] = useState(
    (meeting.agenda || []).map((a) => ({
      title: a.title || "", description: a.description || "",
      duration_minutes: a.duration_minutes || "",
      status: a.status || "pending",
    })),
  );
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const patch = {};
    if (title) patch.title = title;
    if (description) patch.description = description;
    if (duration) patch.duration_minutes = Number(duration);
    if (mode) patch.mode = mode;
    if (videoUrl) patch.video_url = videoUrl;
    if (location) patch.location = location;
    if (replaceAgenda) {
      patch.agenda = agenda
        .filter((a) => (a.title || "").trim())
        .map((a) => ({
          title: a.title.trim(),
          description: a.description || null,
          duration_minutes: a.duration_minutes ? Number(a.duration_minutes) : null,
          status: a.status || "pending",
        }));
    }
    if (!Object.keys(patch).length && !shiftMinutes) {
      toast.error("Nothing to change");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/meetings/series/${meeting.series_id}`,
        { scope, from_meeting_id: scope === "future" ? fromMeetingId : null,
           shift_minutes: Number(shiftMinutes) || 0, patch });
      toast.success(`Updated ${data.updated} occurrence${data.updated === 1 ? "" : "s"} · shifted ${data.shifted}`);
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Series update failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="series-editor-dialog">
        <DialogHeader>
          <DialogTitle>Edit recurring series</DialogTitle>
          <DialogDescription>Change time, agenda, or details across every future occurrence in one save.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border p-3">
            <div className="label-eyebrow mb-2">Scope</div>
            <div className="flex gap-2">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" checked={scope === "future"} onChange={() => setScope("future")} data-testid="series-scope-future" />
                Future occurrences (from this meeting onwards)
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" checked={scope === "all"} onChange={() => setScope("all")} data-testid="series-scope-all" />
                All (including past)
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground italic mt-1">
              Done occurrences are never modified.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Shift start time by (minutes)</Label>
              <Input type="number" value={shiftMinutes} onChange={(e) => setShiftMinutes(e.target.value)}
                     className="h-9 rounded-none" placeholder="e.g. 30 or -15"
                     data-testid="series-shift-minutes" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">New duration (min)</Label>
              <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)}
                     className="h-9 rounded-none" placeholder={String(meeting.duration_minutes || 30)}
                     data-testid="series-duration" />
            </div>
          </div>

          <div>
            <Label className="label-eyebrow mb-1 inline-block">Title (overrides all)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   className="rounded-none" placeholder={meeting.title}
                   data-testid="series-title" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-9 rounded-none" data-testid="series-mode">
                  <SelectValue placeholder="Unchanged" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="video_call">Video call</SelectItem>
                  <SelectItem value="audio_call">Audio / phone</SelectItem>
                  <SelectItem value="in_person">In-person</SelectItem>
                  <SelectItem value="chat_dm">Chat / DM</SelectItem>
                  <SelectItem value="email_thread">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="site_visit">Site visit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Video URL</Label>
              <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                     className="h-9 rounded-none" placeholder="https://…"
                     data-testid="series-video-url" />
            </div>
          </div>

          <div>
            <Label className="label-eyebrow mb-1 inline-block">Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)}
                   className="rounded-none" placeholder="Meeting room / address"
                   data-testid="series-location" />
          </div>

          <div>
            <Label className="label-eyebrow mb-1 inline-block">Description</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                      className="rounded-none" placeholder="Applies to every affected occurrence"
                      data-testid="series-description" />
          </div>

          <div className="border border-border p-3">
            <label className="flex items-center gap-2 mb-2 cursor-pointer">
              <input type="checkbox" checked={replaceAgenda} onChange={(e) => setReplaceAgenda(e.target.checked)}
                     data-testid="series-agenda-toggle" />
              <span className="label-eyebrow">Replace agenda on affected occurrences ({agenda.length} item{agenda.length === 1 ? "" : "s"})</span>
            </label>
            {replaceAgenda && (
              <div className="space-y-2">
                {agenda.map((a, idx) => (
                  <div key={idx} className="flex gap-2 items-start" data-testid={`series-agenda-row-${idx}`}>
                    <Input value={a.title} onChange={(e) => {
                      const n = [...agenda]; n[idx] = { ...n[idx], title: e.target.value }; setAgenda(n);
                    }} placeholder="Item title" className="h-7 text-xs rounded-none" />
                    <Input value={a.description} onChange={(e) => {
                      const n = [...agenda]; n[idx] = { ...n[idx], description: e.target.value }; setAgenda(n);
                    }} placeholder="Notes" className="h-7 text-xs rounded-none" />
                    <button type="button" className="text-muted-foreground hover:text-rose-600 p-1"
                            onClick={() => setAgenda(agenda.filter((_, i) => i !== idx))}
                            data-testid={`series-agenda-remove-${idx}`} aria-label="Remove">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px]"
                        onClick={() => setAgenda([...agenda, { title: "", description: "", duration_minutes: "", status: "pending" }])}
                        data-testid="series-agenda-add">
                  <Plus className="w-3 h-3 mr-1" /> Add item
                </Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="series-editor-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="series-editor-save">
            {busy ? "Saving…" : "Apply to series"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
